import { useCallback } from "react";

import { Form } from "react-final-form";

import { patchOrganizationUser } from "@/api/endpoints/organization";
import { useOrganization } from "@/hooks/useOrganization";
import { User } from "@/types/User";
import AccessControl from "@components/AccessControl";
import Button from "@components/Button";
import Modal, {
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from "@components/Modal";
import { ModalProps } from "@components/Modal/Modal";
import Rhythm from "@components/Rhythm";
import Typography from "@components/Typography";

import ReportSubscriptions from "../ReportSubscriptions";
import SearchSubscriptions from "../SearchSubscriptions";
import UserSettingFields, { USER_SETTINGS_FIELDS } from "../UserSettingFields";

import styles from "./EditUserModal.module.css";

export type EditUserModalProps = {
  user: User;
  title?: string;
  onUpdateUser?: (u: User) => void;
  hideUserSettings?: boolean;
} & ModalProps;

const VISIBLE_USER_FIELDS = Object.values(USER_SETTINGS_FIELDS);

const EditUserModal: React.FunctionComponent<EditUserModalProps> = ({
  show,
  onHide,
  onUpdateUser,
  title = L.EDIT_USER_MODAL_TITLE,
  hideUserSettings = false,
  user,
}) => {
  const { data: orgData } = useOrganization();
  const { uuid: organization_uuid = "" } = orgData?.ok ? orgData.data : {};

  const handleSaveUser = useCallback(
    (_user: User) => {
      return patchOrganizationUser({
        organization_uuid: organization_uuid,
        user_id: _user.id,
        user: _user,
      }).then((response) => {
        if (response.ok && response.data)
          onUpdateUser && onUpdateUser(response.data);
        onHide && onHide();
      });
    },
    [onUpdateUser, organization_uuid, onHide]
  );

  if (!user) return null;

  return (
    <Modal show={show} onHide={onHide}>
      <ModalHeader>
        <ModalTitle>{title}</ModalTitle>
      </ModalHeader>
      <ModalContent>
        {hideUserSettings !== true && (
          <Form onSubmit={handleSaveUser} initialValues={user}>
            {({ handleSubmit, submitting, pristine, invalid }) => (
              <form onSubmit={handleSubmit}>
                <div className={styles.settings}>
                  <UserSettingFields fields={VISIBLE_USER_FIELDS} />
                </div>

                <ModalFooter>
                  <Button variant="tertiary" onClick={onHide}>
                    {L.CANCEL_ACTION}
                  </Button>
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={submitting || invalid || pristine}
                  >
                    {L.EDIT_USER_MODAL_SAVE}
                  </Button>
                </ModalFooter>
              </form>
            )}
          </Form>
        )}
        <div className={styles.scroller}>
          <Rhythm>
            <Typography variant="h3">
              {L.EDIT_USER_MODAL_SEARCH_TITLE}
            </Typography>
            <SearchSubscriptions
              tight
              userId={user.id}
              organizationUuid={organization_uuid}
            />
          </Rhythm>
          <AccessControl>
            <Rhythm>
              <Typography variant="h3">
                {L.EDIT_USER_MODAL_REPORT_TITLE}
              </Typography>
              <ReportSubscriptions
                tight
                userId={user.id}
                organizationUuid={organization_uuid}
              />
            </Rhythm>
          </AccessControl>
        </div>
      </ModalContent>
    </Modal>
  );
};
export default EditUserModal;
